import {
  MOVE_FILES_MAX_COUNT,
  SUBFOLDER_EMPTY_OPTION_VALUE,
  SUBFOLDER_FORBIDDEN_CHARACTERS,
  SUBFOLDER_HEADER_OPTION_VALUE,
  SUBFOLDER_MESSAGES,
  SUBFOLDER_NAME_MAX_LENGTH,
  SUBFOLDER_NONE_OPTION_VALUE,
  SUBFOLDER_SEPARATOR,
} from '@/constants/FILE_SUBFOLDERS'
import {
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_UNAUTHORIZED,
  HTTP_STATUS_UNPROCESSABLE_ENTITY,
} from '@/constants/HTTP_STATUS'
import { splitFileTypeName, unescapeGroupName } from '@/utils/fileTypeFolders'

import type { RecordFile, SubfolderSummary } from '~/types/recordFiles'

export type SubfolderSelectOption = {
  kind: 'create' | 'existing' | 'none' | 'header' | 'empty'
  value: string
  label: string
  count?: number
}

const NATURAL_SORT_OPTIONS: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' }

export function normalizeSubfolderName(value?: string | null): string | null {
  const trimmed = (value ?? '').trim()
  return trimmed || null
}

// D6. Empty is valid: it means "no subfolder".
export function getSubfolderNameError(value?: string | null): string | null {
  const name = normalizeSubfolderName(value)
  if (!name) {
    return null
  }
  if (name.length > SUBFOLDER_NAME_MAX_LENGTH) {
    return SUBFOLDER_MESSAGES.tooLong
  }
  if (SUBFOLDER_FORBIDDEN_CHARACTERS.some((character) => name.includes(character))) {
    return SUBFOLDER_MESSAGES.forbiddenCharacters
  }
  return null
}

export function findExistingSubfolder(name: string, existing: string[]): string | null {
  const normalized = normalizeSubfolderName(name)?.toLowerCase()
  if (!normalized) {
    return null
  }
  return existing.find((candidate) => candidate.toLowerCase() === normalized) ?? null
}

// Subfolders of one file type on this record with their file counts. Deleted
// files are listed in the sidebar but no longer keep a subfolder alive (AC4-8).
export function getSubfoldersForGroup(files: RecordFile[], groupId: number): SubfolderSummary[] {
  const counts = new Map<string, number>()
  files.forEach((file) => {
    if (file.group?.id !== groupId || file.meta?.deleted) {
      return
    }
    const subfolder = normalizeSubfolderName(file.subfolder)
    if (subfolder) {
      counts.set(subfolder, (counts.get(subfolder) ?? 0) + 1)
    }
  })
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, NATURAL_SORT_OPTIONS))
}

// Only the type name is HTML-escaped by the backend; subfolders are stored and
// returned unescaped, so they are used as they come.
export function formatFilePath(fileTypeName: string, subfolder?: string | null): string {
  const typeName = unescapeGroupName(fileTypeName).trim()
  const subfolderName = normalizeSubfolderName(subfolder)
  return subfolderName ? `${typeName} ${SUBFOLDER_SEPARATOR} ${subfolderName}` : typeName
}

export function fileMatchesSearch(file: RecordFile, query: string, includeSubfolder: boolean): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return true
  }
  if ((file.fileName || '').toLowerCase().includes(normalizedQuery)) {
    return true
  }
  return includeSubfolder && (file.subfolder ?? '').toLowerCase().includes(normalizedQuery)
}

// GET suppliers/{slug}/application-configuration exposes the flag as
// `feature.enableSubfolders.display`, the shape of every boolean feature there.
export function isSubfoldersEnabledInConfig(config: unknown): boolean {
  const configWithFlag = config as { enableSubfolders?: { display?: boolean } | null } | null | undefined
  return Boolean(configWithFlag?.enableSubfolders?.display)
}

// Shared by the upload field and the move picker: existing subfolders filtered
// by the typed text, plus the name to offer as "Create" when it is new and valid.
export function filterSubfolderChoices(
  subfolders: SubfolderSummary[],
  query: string,
): { matches: SubfolderSummary[]; createName: string | null; error: string | null } {
  const name = normalizeSubfolderName(query)
  if (!name) {
    return { matches: subfolders, createName: null, error: null }
  }
  const lowerName = name.toLowerCase()
  const matches = subfolders.filter((subfolder) => subfolder.name.toLowerCase().includes(lowerName))
  const error = getSubfolderNameError(name)
  const exists = findExistingSubfolder(name, subfolders.map((subfolder) => subfolder.name)) !== null
  return { matches, createName: error || exists ? null : name, error }
}

// The header and the "None yet" line are only labels inside the dropdown.
export function isSubfolderOptionSelectable(option: Pick<SubfolderSelectOption, 'kind'>): boolean {
  return option.kind !== 'header' && option.kind !== 'empty'
}

export function buildSubfolderSelectOptions({ subfolders, query, fileTypeName, entityTitle }: {
  subfolders: SubfolderSummary[]
  query: string
  fileTypeName: string
  entityTitle: string
}): SubfolderSelectOption[] {
  const { matches, createName } = filterSubfolderChoices(subfolders, query)
  const hasNoSubfolders = subfolders.length === 0
  return [
    ...(createName
      ? [{ kind: 'create' as const, value: createName, label: SUBFOLDER_MESSAGES.createOption(createName) }]
      : []),
    ...(matches.length || hasNoSubfolders
      ? [{
        kind: 'header' as const,
        value: SUBFOLDER_HEADER_OPTION_VALUE,
        label: SUBFOLDER_MESSAGES.sectionHeader(formatFilePath(fileTypeName), entityTitle),
      }]
      : []),
    ...(hasNoSubfolders
      ? [{ kind: 'empty' as const, value: SUBFOLDER_EMPTY_OPTION_VALUE, label: SUBFOLDER_MESSAGES.noSubfoldersYet }]
      : []),
    ...matches.map((subfolder) => ({
      kind: 'existing' as const,
      value: subfolder.name,
      label: subfolder.name,
      count: subfolder.count,
    })),
    {
      kind: 'none' as const,
      value: SUBFOLDER_NONE_OPTION_VALUE,
      label: SUBFOLDER_MESSAGES.noSubfolderOption(splitFileTypeName(fileTypeName).fileType),
    },
  ]
}

type RequestError = {
  response?: {
    status?: number
    data?: { message?: string; errors?: Record<string, string[] | string> }
  }
}

// The subfolder every file is in (null = all directly in the type), or
// undefined when they differ or there are none.
export function getCommonSubfolder(files: RecordFile[]): string | null | undefined {
  if (!files.length) {
    return undefined
  }
  const first = normalizeSubfolderName(files[0].subfolder)
  return files.every((file) => normalizeSubfolderName(file.subfolder) === first) ? first : undefined
}

// "Move to subfolder" in the file "…" menu (AC4-23). A subfolder belongs to one
// file type, so a selection must share one; a file without a type has none.
export function getMoveToSubfolderState({ isEnabled, isGroup, files }: {
  isEnabled: boolean
  isGroup: boolean
  files: RecordFile[]
}): { visible: boolean; disabled: boolean; label: string; disabledTooltip: string } {
  const label = isGroup ? SUBFOLDER_MESSAGES.moveGroupOption : SUBFOLDER_MESSAGES.moveOption
  if (!isEnabled || !files.length) {
    return { visible: false, disabled: true, label, disabledTooltip: '' }
  }
  if (!isGroup) {
    return { visible: Boolean(files[0].group), disabled: false, label, disabledTooltip: '' }
  }
  const groupIds = new Set(files.map((file) => file.group?.id ?? null))
  const isMixed = groupIds.size > 1 || groupIds.has(null)
  const isTooMany = files.length > MOVE_FILES_MAX_COUNT
  let disabledTooltip = ''
  if (isMixed) {
    disabledTooltip = SUBFOLDER_MESSAGES.mixedFileTypes
  } else if (isTooMany) {
    disabledTooltip = SUBFOLDER_MESSAGES.tooManyFiles
  }
  return {
    visible: true,
    disabled: Boolean(disabledTooltip),
    label,
    disabledTooltip,
  }
}

// Message for a failed subfolder request (move here; rename in the Files tab).
// Null on 401: httpClient has already sent the user to the login page.
export function getSubfolderRequestErrorMessage(
  error: unknown,
  fallback: string,
  forbiddenMessage: string = SUBFOLDER_MESSAGES.moveForbidden,
): string | null {
  const response = (error as RequestError | null | undefined)?.response
  if (response?.status === HTTP_STATUS_UNAUTHORIZED) {
    return null
  }
  if (response?.status === HTTP_STATUS_FORBIDDEN) {
    return forbiddenMessage
  }
  if (response?.status === HTTP_STATUS_UNPROCESSABLE_ENTITY) {
    const firstError = Object.values(response.data?.errors ?? {})
      .map((value) => (Array.isArray(value) ? value[0] : value))
      .find(Boolean)
    return firstError || response.data?.message || fallback
  }
  return fallback
}
